import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TablePagination,
  Pagination,
  Stack,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Radio,
  Skeleton,
} from '@mui/material';

import { GridContainer, GridItem, CheckBox, NoContent } from '../../shared';
import { useTable, usePagination, useExpanded, useRowSelect, useGlobalFilter } from 'react-table';
import useStyles from './style';
import SearchBar from '../../SearchBar';
import SortableHeader from './SortableHeader';
import { DEFAULT_PER_PAGE, DEFAULT_PAGE } from '../../../constants/pagination';

/**
 * Lazy-renders row sub-components only when near the viewport.
 * Once rendered, content stays mounted to preserve form state.
 */
const LazyRowContent = React.memo(({ children }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible]);

  return <div ref={ref}>{isVisible ? children : <Skeleton variant="rectangular" height={80} sx={{ m: 1 }} />}</div>;
});

LazyRowContent.displayName = 'LazyRowContent';

/**
 * Resolves a column width definition to a CSS width.
 * Numbers are treated as pixels, anything else (e.g. 'auto' or a percentage) is used as-is.
 * @param {number|string} width - The column width definition
 * @returns {string} A CSS width value
 */
const getColumnWidth = (width) => {
  if (width === undefined || width === null || width === 'auto') {
    return 'auto';
  }

  return typeof width === 'number' ? `${width}px` : width;
};

/**
 * Returns a table
 * By passing pagination, React table will use usePagination hook to handle client-side pagination
 * @param {pagination: Bool}
 * It is not used in this component
 * To use server-side pagination, it needs to pass manualPagination
 * @param {pagination: Bool}
 * @param {manualPagination: Bool}
 *
 * There are two type of server-side pagination: Basic pagination and Table pagination, can only use one of them
 * To use basic pagination, need to pass
 * @param {basicPagination: Bool}
 * @param {pageNumberForBasicPagination: Number}
 * @param {totalPages: Number}
 * @param {onPageChange: Function}
 *
 * To use table pagination
 * @param {totalCount: Number}
 * @param {pageNumberForTablePagination: Number}
 * @param {perPage: Number}
 * @param {totalCount: Number}
 * @param {onPageChange: Function}
 * @param {onPerPageChange: Function}
 *
 * By passing expandable, React table will use useExpanded hook to implement row expanding
 * @param {expandable: Bool}
 * @param {renderRowSubComponent: Function}
 * The renderRowSubComponent is a function to render sub components when an element is expanded
 * @param {autoExpandedItems: [Object<rowId: String, expanded: Bool>]}
 * autoExpandedItems is to pass an array of objects, each object: {rowId: String, expanded: Bool}
 * Example: 
 * [
    {
        "rowId": true
    },
    {
        "1": true
    }
]
 * @param {expandCheck: String}
 * expandCheck is used to check if row.original[expandCheck] has any objects
 *
 * By passing rowSelect, React table will use useRowSelect hook to implement basic row selection
 * @param {rowSelect: Bool}
 * @param {onSetSelectedRows: Function}
 * onSetSelectedRows is a function to pass selectedFlatRows data to other components
 * @param {toggleSelectionRef: Object (a ref object)}
 * toggleSelectionRef is to mutate toggleRowSelected,toggleAllRowsSelected
 * toggleRowSelected: Function(rowPath: String, ?set: Bool) => void
 * - can toggle a row's selected state.
 * toggleAllRowsSelected: Function(?set: Bool) => void
 * - can toggle all rows as selected or not
 *
 * By passing sortable, React table will use useSortBy hook to implement column sorting
 * @param {sortable: Bool}
 * @param {onSortChange: Function}
 * onSortChange is a function to handle sort state changes for server-side sorting
 */

const ReactTable = ({
  columns,
  data,
  pagination,
  manualPagination = false,
  basicPagination = false,
  pageNumberForBasicPagination = DEFAULT_PAGE,
  totalPages = 0,
  totalCount = 0,
  pageNumberForTablePagination = DEFAULT_PAGE - 1,
  perPage = DEFAULT_PER_PAGE,
  onPageChange = () => {},
  onPerPageChange = () => {},
  expandable = false,
  autoExpandedItems = {},
  renderRowSubComponent,
  stickyHeader = false,
  expandAllRowsByDefault = false,
  expandRowOnClick = false,
  tableHeight = 'auto',
  expandCheck = null,
  rowSelect = false,
  onSetSelectedRows,
  toggleSelectionRef = () => {},
  localFilter = false,
  isSingleSelect = false,
  hideHeader = false,
  checkboxIndeterminate = {},
  extraBtn = null,
  bottomExtraBtn = null,
  preSelectedRows = [],
  noContentIcon = null,
  searchPlaceHolder = 'Search',
  rowClassName,
  disablePagination = false,
  enableRowClick = false,
  changeRowsPerPageOptions = false,
  sortable = false,
  onSortChange = () => {},
  sortBy = null, // Current sort state: { id: 'columnId', desc: boolean }
  tableProps = {},
}) => {
  const handleSelectedRows = () => {
    const preIds = preSelectedRows.map((item) => item.id);
    const obj = {};
    for (let index in data) {
      if (preIds.includes(data[index].id)) {
        obj[index] = true;
      }
    }
    return obj;
  };
  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: manualPagination,
      initialState: {
        expanded: autoExpandedItems,
        selectedRowIds: handleSelectedRows(),
      },
      stateReducer: (newState, action) => {
        if (isSingleSelect && action.type === 'toggleRowSelected') {
          newState.selectedRowIds = {
            [action.id]: true,
          };
        }

        return newState;
      },
    },
    localFilter ? useGlobalFilter : '',
    expandable ? useExpanded : '',
    pagination ? usePagination : '',
    rowSelect ? useRowSelect : '',
    (hooks) => {
      hooks.visibleColumns.push((columns) => {
        if (rowSelect) {
          return [
            {
              id: `selection ${rowSelect}`,
              Header: () => null,
              width: 'auto',
              Cell: ({ row }) => {
                if (row.original.isChecked) {
                  return isSingleSelect ? (
                    <Radio {...row.getToggleRowSelectedProps()} checked />
                  ) : (
                    <CheckBox {...row.getToggleRowSelectedProps()} checked />
                  );
                }
                return isSingleSelect ? (
                  <Radio
                    {...row.getToggleRowSelectedProps()}
                    onClick={() => {
                      const check = row.isSelected;

                      toggleAllRowsSelected(false);
                      toggleRowSelected(row.id, !check);
                    }}
                  />
                ) : (
                  <CheckBox
                    {...row.getToggleRowSelectedProps()}
                    indeterminate={checkboxIndeterminate[row.id] || false}
                  />
                );
              },
            },
            ...columns,
          ];
        }
        return [...columns];
      });
    },
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setGlobalFilter,
    rows,
    page,
    state: { selectedRowIds },
    visibleColumns,
    selectedFlatRows,
    toggleRowSelected,
    toggleAllRowsSelected,
    toggleAllRowsExpanded,
  } = tableInstance;
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(
    basicPagination ? pageNumberForBasicPagination : pageNumberForTablePagination,
  );
  const [rowsPerPage, setRowsPerPage] = useState(perPage);
  const [manualExpandedRows, setManualExpandedRows] = useState({});
  const expandedRowsInitializedRef = useRef(-1);
  const rowExpansionKey = expandAllRowsByDefault ? rows.map((row) => row.id).join('|') : '';

  useEffect(() => {
    setRowsPerPage(perPage);
  }, [perPage]);

  const rowsPerPageChangeHandler = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(DEFAULT_PAGE - 1);
  };

  useEffect(() => {
    if (rowSelect) {
      const flatRows = selectedFlatRows.map((d) => d.original);
      onSetSelectedRows(flatRows);
    }
  }, [selectedRowIds, rows]);
  useEffect(() => {
    if (!basicPagination) {
      setCurrentPage(pageNumberForTablePagination);
    }
  }, [pageNumberForTablePagination]);
  useEffect(() => {
    toggleSelectionRef.current = {
      toggleAllRowsSelected,
      toggleRowSelected,
    };
  }, []);

  useEffect(() => {
    if (expandable && expandAllRowsByDefault && typeof toggleAllRowsExpanded === 'function') {
      if (expandedRowsInitializedRef.current === rowExpansionKey) {
        return;
      }

      if (expandRowOnClick) {
        setManualExpandedRows(
          rows.reduce((expandedRows, row) => {
            expandedRows[row.id] = true;
            return expandedRows;
          }, {}),
        );
        expandedRowsInitializedRef.current = rowExpansionKey;
        return;
      }

      toggleAllRowsExpanded(true);
      expandedRowsInitializedRef.current = rowExpansionKey;
    }
  }, [expandable, expandAllRowsByDefault, expandRowOnClick, rowExpansionKey, rows, toggleAllRowsExpanded]);

  const getIsRowExpanded = (row) => (expandRowOnClick ? Boolean(manualExpandedRows[row.id]) : row.isExpanded);

  const toggleExpandableRow = (row, expanded) => {
    if (expandRowOnClick) {
      setManualExpandedRows((currentExpandedRows) => ({
        ...currentExpandedRows,
        [row.id]: expanded,
      }));
      return;
    }

    row.toggleRowExpanded?.(expanded);
  };

  const expandElement = (row) => {
    if (expandCheck && row.original[expandCheck]?.length === 0) {
      return;
    }

    const isExpanded = getIsRowExpanded(row);

    if (isExpanded) {
      return (
        <TableRow className={`${classes.accordionBody} ${isExpanded ? classes.accordionExpanded : ''}`}>
          <TableCell colSpan={visibleColumns.length}>
            <LazyRowContent>{renderRowSubComponent({ row })}</LazyRowContent>
          </TableCell>
        </TableRow>
      );
    }
  };

  const shouldIgnoreExpandableRowClick = (target) => {
    if (target?.closest?.('[data-row-expander="true"]')) {
      return false;
    }

    return Boolean(
      target?.closest?.('button, a, input, textarea, select, label, [role="button"], [data-prevent-row-expand="true"]'),
    );
  };

  const expandablePaginationElement = (row, i, rowClassName) => {
    const rowProps = row.getRowProps();
    const rowId = row.original?.scrollId;
    const isExpanded = getIsRowExpanded(row);
    if (expandRowOnClick) {
      row.isExpanded = isExpanded;
      row.toggleRowExpanded = (expanded = !isExpanded) => toggleExpandableRow(row, expanded);
    }
    const handleExpandableRowClick = (event) => {
      if (!expandRowOnClick || shouldIgnoreExpandableRowClick(event.target)) {
        return;
      }

      toggleExpandableRow(row, !isExpanded);
    };
    // to enable whole line clickable to expand, attach {...rowExpand} in TableCell
    // const rowExpand = row.getToggleRowExpandedProps();

    return (
      <React.Fragment key={rowProps.key}>
        <TableRow
          key={i}
          {...rowProps}
          id={rowId}
          onClick={expandRowOnClick ? handleExpandableRowClick : undefined}
          sx={expandRowOnClick ? { cursor: 'pointer' } : undefined}
          className={`${rowClassName?.(row) ? classes.error : ''} ${isExpanded ? classes.accordionExpanded : ''}`}
        >
          {row.cells.map((cell, i) =>
            cell.column.hideHeaderAndColumn ? null : (
              <TableCell
                key={i}
                {...cell.getCellProps()}
                sx={{
                  padding: cell.column.isPadding ? cell.column.padding : '20px 24px',
                }}
              >
                {cell.render('Cell')}
              </TableCell>
            ),
          )}
        </TableRow>
        {expandElement(row)}
      </React.Fragment>
    );
  };

  const nonExpandElement = (row, i) => (
    <TableRow
      {...row.getRowProps()}
      key={i}
      id={row.original?.scrollId}
      onClick={enableRowClick ? row.original.onClick : undefined} // Conditionally attach the onClick handler
      className={`${rowClassName?.(row) ? rowClassName?.(row) : ''}`} // Apply the row class name
    >
      {row.cells.map((cell, i) =>
        cell.column.hideHeaderAndColumn ? null : (
          <TableCell
            key={i}
            {...cell.getCellProps()}
            sx={{
              padding: cell.column.isPadding ? cell.column.padding : '20px 24px',
              width: getColumnWidth(cell.column.width),
            }}
          >
            {cell.render('Cell')}
          </TableCell>
        ),
      )}
    </TableRow>
  );

  useEffect(() => {
    if (manualPagination && basicPagination) {
      onPageChange(currentPage);
    }

    if (manualPagination) {
      onPageChange(currentPage + 1);
      onPerPageChange(rowsPerPage);
    }
  }, [currentPage, manualPagination, rowsPerPage]);

  return (
    <>
      <Box>
        {localFilter && (
          <Box className={classes.searchWrap}>
            <SearchBar
              onSearch={(value) => {
                setGlobalFilter(value);
              }}
              resetFilter={() => setGlobalFilter('')}
              placeholder={searchPlaceHolder}
            />
            {extraBtn ?? extraBtn}
          </Box>
        )}
        <TableContainer
          component={Paper}
          sx={
            stickyHeader
              ? {
                  maxHeight: tableHeight,
                  overflowY: 'auto',
                }
              : {}
          }
        >
          <Table {...getTableProps()} {...tableProps}>
            {hideHeader || (rows.length === 0 && localFilter) ? null : (
              <TableHead className={`${classes.tableHeader}`}>
                {headerGroups.map((headerGroup, i) => (
                  <TableRow key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, i) => {
                      if (!column?.hideHeaderAndColumn) {
                        const isSortable = sortable && column.canSort;

                        return (
                          <TableCell
                            key={i}
                            {...column.getHeaderProps()}
                            className={stickyHeader ? classes.stickyHeaderCell : ''}
                            sx={{
                              padding: column.isPadding ? column.padding : '12px 24px',
                              width: getColumnWidth(column.width),
                            }}
                          >
                            <SortableHeader
                              column={column}
                              onSortChange={onSortChange}
                              sortBy={sortBy}
                              isSortable={isSortable}
                            />
                          </TableCell>
                        );
                      }
                    })}
                  </TableRow>
                ))}
              </TableHead>
            )}
            {!pagination && (
              <TableBody {...getTableBodyProps()} className={`${classes.tableBody}`}>
                {!expandable &&
                  rows.map((row, i) => {
                    prepareRow(row);
                    return nonExpandElement(row, i);
                  })}
                {expandable &&
                  rows.map((row, i) => {
                    prepareRow(row);
                    return expandablePaginationElement(row, i, rowClassName);
                  })}
              </TableBody>
            )}
            {pagination && (
              <TableBody {...getTableBodyProps()} className={`${classes.tableBody}`}>
                {!expandable &&
                  page.map((row, i) => {
                    prepareRow(row);
                    return nonExpandElement(row, i);
                  })}
                {expandable &&
                  page.map((row, i) => {
                    prepareRow(row);
                    return expandablePaginationElement(row, i, rowClassName);
                  })}
              </TableBody>
            )}
          </Table>
          {!pagination && rows.length === 0 && localFilter && (
            <NoContent
              icon={noContentIcon}
              title="No Result Found"
              desc={<>Please try again using new search criteria</>}
            />
          )}
        </TableContainer>
      </Box>
      {pagination && manualPagination && !basicPagination && (
        <Box className={bottomExtraBtn ? classes.searchWrap : ''}>
          {bottomExtraBtn ?? bottomExtraBtn}
          <TablePagination
            component={Paper}
            count={totalCount}
            page={currentPage}
            onPageChange={(e, value) => setCurrentPage(value)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={changeRowsPerPageOptions ? [30, 60, 90, 120] : [10, 20, 50, 100]}
            onRowsPerPageChange={rowsPerPageChangeHandler}
            disabled={disablePagination}
          />
        </Box>
      )}

      {pagination && manualPagination && basicPagination && (
        <GridContainer spacing={3} mt={3} className={classes.manualPagination}>
          <GridItem xs={12} display="flex" justifyContent="center">
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                variant="outlined"
                classes={{ ul: classes.ul }}
                page={currentPage}
                onChange={(e, value) => setCurrentPage(value)}
                disabled={disablePagination}
              />
            </Stack>
          </GridItem>
        </GridContainer>
      )}
    </>
  );
};
export default React.memo(ReactTable);
